export function getBookingDetailVisibility(role = "customer") {
  const normalizedRole = String(role || "customer").toLowerCase();
  const isCustomer = ["customer", "corporate"].includes(normalizedRole);
  const isArtist = normalizedRole === "artist";
  const isAgency = normalizedRole === "agency";
  const isOperations = ["admin", "subadmin", "manager"].includes(normalizedRole);
  const canRequestRefund = normalizedRole === "customer" || isArtist;

  return {
    showBookingTimeline: true,
    showPaymentTimeline: isOperations,
    showCustomerPaymentSchedule: isCustomer,
    showPayoutLedger: isArtist || isAgency || isOperations,
    showMutualRefund: canRequestRefund,
    showInternalPaymentBreakdown: isOperations,
    showArtistPayoutSummary: isArtist || isAgency || isOperations,
  };
}

export function canViewArtistContact(role, contactUnlocked) {
  const normalizedRole = String(role || "").toLowerCase();
  return ["admin", "subadmin", "manager", "artist"].includes(normalizedRole) ||
    (Boolean(contactUnlocked) && ["customer", "corporate", "agency"].includes(normalizedRole));
}

export function shouldShowCustomerContactCard(role) {
  return !["customer", "corporate"].includes(String(role || "").toLowerCase());
}

export function getBookingAttention(booking = {}, role = "customer") {
  const status = String(booking.status || "").toLowerCase();
  const normalizedRole = String(role || "").toLowerCase();
  const isCustomer = ["customer", "corporate"].includes(normalizedRole);
  const isArtist = normalizedRole === "artist";

  if (["cancelled", "rejected", "auto_expired"].includes(status)) {
    return {
      tone: "muted",
      title: status === "auto_expired" ? "Booking request expired" : `Booking ${status}`,
      body: "Review the booking timeline and contact support if you need help.",
    };
  }
  if (status === "pending_artist") {
    return isArtist
      ? { tone: "warning", title: "Your response is needed", body: "Review the event details and respond to this request from your artist dashboard.", actionHref: "/artist", actionLabel: "Open artist dashboard" }
      : { tone: "warning", title: "Awaiting artist confirmation", body: "The booking is on hold until the artist responds." };
  }
  if (status === "pending_payment") {
    return isCustomer
      ? { tone: "warning", title: "Payment is needed", body: "Complete the next payment installment to keep the booking on track.", actionHref: "#bd-payment-schedule", actionLabel: "View payment schedule" }
      : { tone: "warning", title: "Payment is pending", body: "The booking will progress when the required payment is received." };
  }
  if (["completed", "reviewed", "completed_by_artist"].includes(status)) {
    return { tone: "success", title: "Event completed", body: "This booking has reached its completion stage." };
  }
  if (["confirmed", "started"].includes(status)) {
    return { tone: "success", title: "Booking confirmed", body: "The booking is confirmed. Check the event details and payment schedule below." };
  }
  return { tone: "neutral", title: "Booking status", body: "Review the latest booking details below." };
}

export function getBookingProgress(booking = {}, paymentSummary = {}, payouts = [], role = "customer") {
  const status = String(booking.status || "").toLowerCase();
  const confirmed = ["confirmed", "started", "completed_by_artist", "completed", "reviewed"].includes(status);
  const completed = ["completed", "reviewed"].includes(status);
  const normalizedRole = String(role || "customer").toLowerCase();
  const showPayout = ["artist", "agency", "admin", "subadmin", "manager"].includes(normalizedRole);
  const today = new Date().toISOString().slice(0, 10);
  const eventDate = String(booking.event_date || "").slice(0, 10);
  const payoutTotal = (payouts || []).reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const payoutDone = ["complete", "paid"].includes(String(paymentSummary.payoutStatus || "").toLowerCase()) ||
    (paymentSummary.artistPayableTotal > 0 && payoutTotal >= paymentSummary.artistPayableTotal);
  const steps = [
    { key: "selected", label: "Artist selected", done: Boolean(booking.artist_id), date: booking.created_at },
    {
      key: "confirmation",
      label: status === "pending_artist" ? "Artist response" : status === "pending_payment" ? "Booking advance" : "Booking confirmed",
      done: confirmed,
      date: booking.confirmed_at,
      waiting: status === "pending_artist" || status === "pending_payment",
    },
    { key: "event", label: "Event day", done: Boolean(eventDate && eventDate < today), date: eventDate, upcoming: Boolean(eventDate && eventDate >= today) },
    { key: "completed", label: "Completed", done: completed, date: booking.completed_at },
  ];
  if (showPayout) {
    steps.splice(steps.length - 1, 0, { key: "payout", label: "Artist payout", done: payoutDone, date: payouts?.[0]?.paid_on });
  }
  const currentIndex = steps.findIndex((step) => !step.done);
  return steps.map((step, index) => ({
    ...step,
    current: !completed && index === (currentIndex < 0 ? steps.length - 1 : currentIndex),
  }));
}

export function getPaymentAttemptDisplayState(attempt = {}, now = Date.now()) {
  const status = String(attempt.status || "pending").toLowerCase();
  if (status === "completed") return { label: "Successful", tint: "green", isDelayed: false };
  if (status === "failed") return { label: "Failed", tint: "red", isDelayed: false };

  const createdAt = Date.parse(attempt.created_at || "");
  const isDelayed = Number.isFinite(createdAt) && now - createdAt >= 30000;
  return {
    label: isDelayed ? "Confirmation delayed" : "In progress",
    tint: "gold",
    isDelayed,
  };
}

export function getPaymentAttemptTimestamp(attempt = {}) {
  const status = String(attempt.status || "pending").toLowerCase();
  const isTerminal = status === "completed" || status === "failed";
  const timestamp = isTerminal
    ? attempt.verified_at || attempt.created_at
    : attempt.created_at;
  const date = new Date(timestamp || "");
  if (!Number.isFinite(date.getTime())) return null;

  return {
    label: isTerminal ? "Status updated" : "Started",
    value: new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(date),
  };
}

export function summarizeBookingPayment(booking = {}, schedule = null) {
  const financial = booking?.financial_summary || {};
  const total = Number(
    financial.total_booking_value ??
    booking?.pricing?.total ??
    booking?.total ??
    schedule?.total ??
    0,
  );
  const paid = Number(
    financial.customer_received ??
    booking?.amount_paid ??
    booking?.paid_amount ??
    schedule?.amount_received ??
    0,
  );
  const due = Number(financial.customer_due ?? Math.max(total - paid, 0));
  const status = financial.payment_status || booking?.payment_status || (paid >= total ? "paid" : paid > 0 ? "partial" : "unpaid");

  const milestones = Array.isArray(schedule?.milestones) ? schedule.milestones : [];
  const paidMilestoneStatuses = ["paid", "completed", "confirmed"];
  const nextMilestoneSource = milestones.find((milestone) => !paidMilestoneStatuses.includes((milestone?.status || "").toLowerCase())) || financial.next_milestone || null;
  const nextMilestone = nextMilestoneSource ? {
    label: nextMilestoneSource.label || "Next installment",
    amount: Number(nextMilestoneSource.amount || nextMilestoneSource.amount_due || 0),
    due_date: nextMilestoneSource.due_date || nextMilestoneSource.date || null,
    due_label: nextMilestoneSource.due_label || null,
    status: nextMilestoneSource.status || "pending",
  } : null;
  const finalMilestoneSource = milestones[milestones.length - 1] || null;
  const finalMilestone = finalMilestoneSource ? {
    label: finalMilestoneSource.label || "Final installment",
    amount: Number(finalMilestoneSource.amount || finalMilestoneSource.amount_due || 0),
    due_date: finalMilestoneSource.due_date || finalMilestoneSource.date || null,
    due_label: finalMilestoneSource.due_label || null,
    paid_on: finalMilestoneSource.paid_on || null,
    status: finalMilestoneSource.status || "pending",
  } : null;

  const eventParts = [
    booking?.venue,
    booking?.city,
    booking?.event_date,
    booking?.event_time,
  ].filter(Boolean);

  const eventSummary = eventParts.length
    ? [
        [booking?.venue, booking?.city].filter(Boolean).join(", "),
        booking?.event_date,
        booking?.event_time,
      ].filter(Boolean).join(" · ")
    : "—";

  const commissionPct = Number(financial.booktalent_commission_pct ?? financial.commission_pct ?? booking?.pricing?.commission_pct ?? booking?.pricing?.platform_fee_pct ?? 15);
  const gstPct = 0;
  const rawCommission = Number(financial.booktalent_commission ?? financial.commission ?? booking?.pricing?.booktalent_commission ?? booking?.pricing?.platform_fee ?? 0);
  const rawGstValue = 0;
  const isServiceArtist = Boolean(financial.is_service_artist ?? booking?.pricing?.is_service_artist);
  const explicitBaseServiceCost = Number(financial.base_service_cost ?? booking?.pricing?.base_service_cost ?? 0);
  const pricingBase = Number(
    isServiceArtist
      ? booking?.pricing?.artist_fee
      : booking?.pricing?.platform_fee_net ?? booking?.pricing?.platform_fee,
  );
  const baseServiceCost = explicitBaseServiceCost > 0
    ? explicitBaseServiceCost
    : pricingBase > 0
      ? pricingBase
      : total;
  const effectiveBaseCost = baseServiceCost > 0 ? baseServiceCost : total;
  const computedCommission = Number((effectiveBaseCost * (commissionPct / 100)).toFixed(2));
  const computedGstValue = Number((effectiveBaseCost * (gstPct / 100)).toFixed(2));
  const booktalentShareValue = Number(financial.booktalent_share ?? booking?.pricing?.booktalent_share ?? null);
  const booktalentCommission = rawCommission > 0
    ? rawCommission
    : (booktalentShareValue > 0
      ? Number((booktalentShareValue / (1 + gstPct / 100)).toFixed(2))
      : computedCommission);
  const gstValue = rawGstValue > 0
    ? rawGstValue
    : (booktalentShareValue > 0
      ? Number(Math.max(booktalentShareValue - booktalentCommission, 0).toFixed(2))
      : computedGstValue);
  const booktalentShare = Number(
    financial.booktalent_share ??
    booking?.pricing?.booktalent_share ??
    Math.max(booktalentCommission + gstValue, 0),
  );
  const artistBaseShare = Number(Math.max(effectiveBaseCost - booktalentCommission, 0).toFixed(2));
  const artistPayableTotal = Number(financial.artist_payable_total ?? booking?.pricing?.artist_payable ?? (artistBaseShare || 0));
  const artistPayableAccrued = Number(financial.artist_payable_accrued ?? financial.artist_paid ?? booking?.pricing?.artist_payable_paid ?? 0);
  const artistPending = Number(financial.artist_pending ?? booking?.pricing?.artist_payable_remaining ?? Math.max(artistPayableTotal - artistPayableAccrued, 0));
  const payoutStatus = financial.payout_status || booking?.artist_payout_status || (artistPayableTotal > 0 && artistPending <= 0 ? "paid" : artistPayableTotal > 0 ? "pending" : "not_applicable");

  const ledgerEntries = milestones.map((milestone, index) => {
    const amount = Number(milestone.amount || milestone.amount_due || 0);
    const date = milestone.due_date || milestone.date || milestone.paid_on || null;
    const status = String(milestone.status || (index === 0 && paid > 0 ? "paid" : "pending")).toLowerCase();
    const gstDeduction = Number((amount * (gstPct / 100)).toFixed(2));
    const commissionDeduction = index === 0 && commissionPct > 0 ? Number(booktalentCommission.toFixed(2)) : 0;
    const artistPayout = Number(Math.max(amount - gstDeduction - commissionDeduction, 0).toFixed(2));
    const customerCharge = Number((amount + gstDeduction).toFixed(2));

    return {
      label: milestone.label || `Installment ${index + 1}`,
      date,
      amount,
      gstDeduction,
      commissionDeduction,
      artistPayout,
      customerCharge,
      status,
      isFirst: index === 0,
      hasCommission: index === 0 && commissionDeduction > 0,
    };
  });

  const paidLedgerEntries = ledgerEntries.filter((entry) => ["paid", "completed", "confirmed"].includes(entry.status));
  const pendingLedgerEntries = ledgerEntries.filter((entry) => !["paid", "completed", "confirmed"].includes(entry.status));
  const totalReceived = Number(paidLedgerEntries.reduce((total, entry) => total + entry.amount, 0).toFixed(2));
  const totalGstRemitted = Number(paidLedgerEntries.reduce((total, entry) => total + entry.gstDeduction, 0).toFixed(2));
  const totalCommissionTaken = Number(paidLedgerEntries.reduce((total, entry) => total + (entry.isFirst ? entry.commissionDeduction : 0), 0).toFixed(2));
  const totalArtistPayoutSoFar = Number(paidLedgerEntries.reduce((total, entry) => total + entry.artistPayout, 0).toFixed(2));
  const artistAmountStillPending = Number(pendingLedgerEntries.reduce((total, entry) => total + entry.artistPayout, 0).toFixed(2));

  const paymentRunningTotals = {
    totalReceived,
    totalGstRemitted,
    totalCommissionTaken,
    totalArtistPayoutSoFar,
    artistAmountStillPending,
    totalCommission: totalCommissionTaken,
  };

  const costOverview = [
    { label: "Base Service Cost", amount: effectiveBaseCost },
    { label: `GST @ ${gstPct}%`, amount: gstValue },
    { label: "Total Payable by Customer", amount: Number((effectiveBaseCost + gstValue).toFixed(2)) },
    { label: "BookTalent Commission", amount: booktalentCommission, show: commissionPct > 0 },
    { label: "Artist Base Share", amount: artistBaseShare },
  ].filter((row) => row.amount !== 0 && (row.show === undefined || row.show));

  return {
    total,
    paid,
    due,
    status,
    payoutStatus,
    booktalentShare,
    booktalentCommission,
    gstValue,
    commissionPct,
    gstPct,
    artistPayableTotal,
    artistPayableAccrued,
    artistPending,
    paymentCompletionPct: Number(financial.payment_completion_pct ?? (total > 0 ? (paid / total) * 100 : 0)),
    payoutCompletionPct: Number(financial.payout_completion_pct ?? (artistPayableTotal > 0 ? (Math.max(artistPayableTotal - artistPending, 0) / artistPayableTotal) * 100 : 0)),
    nextMilestone,
    finalMilestone,
    eventSummary,
    customerBillValue: effectiveBaseCost,
    customerBillWithGst: Number((effectiveBaseCost + gstValue).toFixed(2)),
    costOverview,
    ledgerEntries,
    runningTotals: paymentRunningTotals,
  };
}

export function describePaymentStructure(summary = {}) {
  const safeTotal = Number(summary.total || 0);
  const safeGstPct = 0;
  const safeBooktalentShare = Number(summary.booktalentShare || 0);
  const safeCommission = Number(summary.booktalentCommission || 0);
  const safeArtistPayable = Number(summary.artistPayableTotal || 0);
  const safeArtistPending = Number(summary.artistPending || 0);
  const safePaid = Number(summary.paid || 0);
  const safeDue = Number(summary.due || 0);

  const commissionLabel = summary.commissionPct ? `${summary.commissionPct}%` : "the agreed";
  const gstLine = `The customer-facing booking value is ₹${(summary.customerBillWithGst || safeTotal).toLocaleString("en-IN")}, based on the booking amount of ₹${(summary.customerBillValue || safeTotal).toLocaleString("en-IN")}.`;
  const serviceLine = `For a BookTalent service artist, the ${commissionLabel} service fee commission is recorded separately. The artist payout is ₹${safeArtistPayable.toLocaleString("en-IN")}.`;
  const paymentLine = safePaid > 0 || safeDue > 0
    ? `So far, ₹${safePaid.toLocaleString("en-IN")} has been received and ₹${safeDue.toLocaleString("en-IN")} is still due. On every installment, our share is deducted and the rest is created as the artist payout.`
    : "No payment has been received yet. On every installment, our share is deducted and the rest is created as the artist payout.";

  return `${gstLine} ${serviceLine} ${paymentLine}`;
}
